import {Box, Card, CardContent, Chip, Collapse, IconButton, Link, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import React from "react";
import {RiskReportTypes} from "../../types";
import {
    AccountBalance,
    ExpandMore
} from '@mui/icons-material';
import palette from "../../theme/palette";
import {CellNote, EmptyState, ReportTable, isHttpsUrl} from "./report-table";
import {FilteredCharges} from "./filtered-charges";

interface Props {
    assessmentData : RiskReportTypes.RiskResponse;
    handleDownload : boolean
    // The org has the filtered-charges reveal on (intakeRiskAssessment.filteredCharges.enabled).
    showFilteredCharges?: boolean;
}

// `findings.publicRecords` is keyed by state ("CT", "NY", "MA", …), each an
// array of court records. Two CT shapes exist in saved reports:
//   convictions — defendant / docketNo / sentenced / charges[]
//   pending     — defendantName / docketNumber / status / statusDate / hearingType
// Charges (present when the docket-detail fetch succeeded) carry statute,
// type ("Misdemeanor" | "Felony"), classType ("A".."D"), offenseDate, plea and
// verdictFinding. Every field is optional; older reports carry less.
export interface Charge {
    description?: string;
    statute?: string;
    type?: string;
    classType?: string;
    offenseDate?: string;
    plea?: string;
    verdictFinding?: string;
}

export interface CriminalRecord {
    state: string;
    defendant: string;
    birthYear?: string | number;
    court?: string;
    docket?: string;
    disposition?: string;
    // Sentencing date for convictions; next hearing (type + date) for pending cases.
    nextStep: string;
    caseDetailUrl?: string;
    charges: Charge[];
}

// Scraped fields are strings (or a number for birthYear) in every saved report,
// but the payload is untyped: anything else renders as '' rather than
// "[object Object]".
const text = (value: unknown): string =>
    typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '';

export const flattenPublicRecords = (publicRecords: unknown): CriminalRecord[] => {
    if (!publicRecords || typeof publicRecords !== 'object') return [];
    return Object.entries(publicRecords as Record<string, unknown>).flatMap(([state, list]) =>
        (Array.isArray(list) ? list : [])
            .filter((record) => record && typeof record === 'object')
            .map((record: any): CriminalRecord => ({
                state,
                defendant: text(record.defendantName) || text(record.defendant) || 'N/A',
                birthYear: text(record.birthYear) || undefined,
                court: text(record.court) || undefined,
                docket: text(record.docketNo) || text(record.docketNumber) || undefined,
                disposition: text(record.disposition) || undefined,
                nextStep: text(record.sentenced) || [text(record.hearingType), text(record.statusDate)].filter(Boolean).join(' '),
                caseDetailUrl: text(record.caseDetailUrl) || undefined,
                charges: Array.isArray(record.charges)
                    ? record.charges
                        .filter((charge: unknown) => charge && typeof charge === 'object')
                        .map((charge: any): Charge => ({
                            description: text(charge.description),
                            statute: text(charge.statute),
                            type: text(charge.type),
                            classType: text(charge.classType),
                            offenseDate: text(charge.offenseDate),
                            plea: text(charge.plea),
                            verdictFinding: text(charge.verdictFinding),
                        }))
                    : [],
            })),
    );
};

export const formatChargeClass = (charge: Charge): string =>
    [charge.type, charge.classType].filter(Boolean).join(' ');

// "53a-61 · Misdemeanor A" — statute and class share the charge cell so the
// table fits a laptop viewport without a horizontal scroll (twelve columns
// did not).
export const formatChargeDetail = (charge: Charge): string =>
    [charge.statute, formatChargeClass(charge)].filter(Boolean).join(' · ');

// Colour carries meaning only: convictions red, open cases amber, everything
// else (nolle, dismissed, not guilty) plain. "Not guilty" contains "guilty",
// so it has to be ruled out before the conviction test.
const dispositionSx = (disposition?: string) => {
    const value = (disposition ?? '').toLowerCase();
    if (/\bnot guilty\b|\bacquit/.test(value)) return {};
    if (/\bguilty\b|\bconvict/.test(value)) return { color: '#B71D18', fontWeight: 600 };
    if (/pending|open|active/.test(value)) return { color: '#B76E00', fontWeight: 600 };
    return {};
};

const DASH = '—';

export const PublicRecords: React.FC<Props> = ({
                                                   assessmentData,
                                                   handleDownload,
                                                   showFilteredCharges = false
                                               }) => {

    const { findings } = assessmentData?.riskAssessmentInfo ?? {};
    // See news-articles.tsx — `findings` can be missing on stored reports.
    const records = flattenPublicRecords(findings?.publicRecords);
    const chargeCount = records.reduce((total, record) => total + record.charges.length, 0);
    // Only worth a column when the search spanned more than one state.
    const showState = new Set(records.map((record) => record.state)).size > 1;

    // `handleDownload` is true in the live page and false in the detached PDF
    // render. Collapsible in the live view only, and expanded by default so
    // the records are visible without a click; a PDF can't be expanded after
    // the fact, so the download render always shows them.
    const isLiveView = handleDownload;
    const [expanded, setExpanded] = React.useState<boolean>(true);
    const collapsible = isLiveView && records.length > 0;
    const showRecords = !isLiveView || expanded;

    return(
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
                <Box
                    className={'pdf-keep-with-next'}
                    sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: collapsible ? 'pointer' : 'default' }}
                    onClick={() => collapsible && setExpanded(!expanded)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: palette.darkBlue, p: 1, borderRadius: 1, mr: 2 }}>
                        <AccountBalance sx={{ color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={500}>
                            Criminal Records
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Court records matched to the subject's name and birth year
                        </Typography>
                    </Box>
                    {records.length > 0 && (
                        <Chip
                            label={`${records.length} RECORDS${chargeCount > 0 ? ` · ${chargeCount} CHARGES` : ''}`}
                            sx={{bgcolor: '#eb32231a', color: '#EB3223'}}
                            variant="outlined"
                            size="small"
                        />
                    )}
                    {collapsible && (
                        <IconButton size="small" sx={{ ml: 1 }}>
                            <ExpandMore sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </IconButton>
                    )}

                </Box>

                {records.length > 0 ? (
                    <Collapse in={showRecords}>
                        {/* One <tbody> per record: the record's cells span its
                            charge rows, and the PDF slicer keeps each record on
                            one page. Zebra striping is per record for the same
                            reason. */}
                        <ReportTable aria-label="Criminal records">
                            <TableHead className={'pdf-keep-with-next'}>
                                <TableRow>
                                    {showState && <TableCell>State</TableCell>}
                                    <TableCell>Defendant</TableCell>
                                    <TableCell>Birth Yr</TableCell>
                                    <TableCell>Court</TableCell>
                                    <TableCell>Docket</TableCell>
                                    <TableCell>Disposition</TableCell>
                                    <TableCell>Sentenced / Hearing</TableCell>
                                    <TableCell sx={{ width: '34%' }}>Charge</TableCell>
                                    <TableCell>Offense Date</TableCell>
                                    <TableCell>Finding</TableCell>
                                </TableRow>
                            </TableHead>
                            {records.map((record, recordIndex) => {
                                const rows: Array<Charge | null> = record.charges.length > 0 ? record.charges : [null];
                                const span = rows.length;
                                return (
                                    <TableBody key={`${record.docket ?? 'record'}-${recordIndex}`} className={'pdf-avoid-break'}>
                                        {rows.map((charge, chargeIndex) => {
                                            const detail = charge ? formatChargeDetail(charge) : '';
                                            return (
                                            <TableRow key={chargeIndex}>
                                                {chargeIndex === 0 && (
                                                    <>
                                                        {showState && <TableCell rowSpan={span}>{record.state}</TableCell>}
                                                        <TableCell rowSpan={span} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{record.defendant}</TableCell>
                                                        <TableCell rowSpan={span}>{record.birthYear ?? DASH}</TableCell>
                                                        <TableCell rowSpan={span}>{record.court || DASH}</TableCell>
                                                        <TableCell rowSpan={span} sx={{ whiteSpace: 'nowrap' }}>
                                                            {isLiveView && isHttpsUrl(record.caseDetailUrl) ? (
                                                                <Link
                                                                    href={record.caseDetailUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    underline="hover"
                                                                    sx={{ color: palette.darkBlue, fontWeight: 500 }}
                                                                >
                                                                    {record.docket || 'View case'}
                                                                </Link>
                                                            ) : (record.docket || DASH)}
                                                        </TableCell>
                                                        <TableCell rowSpan={span} sx={dispositionSx(record.disposition)}>{record.disposition || DASH}</TableCell>
                                                        <TableCell rowSpan={span} sx={{ whiteSpace: 'nowrap' }}>{record.nextStep || DASH}</TableCell>
                                                    </>
                                                )}
                                                {charge ? (
                                                    <>
                                                        <TableCell>
                                                            {charge.description || DASH}
                                                            {detail && <CellNote sx={{ whiteSpace: 'nowrap' }}>{detail}</CellNote>}
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{charge.offenseDate || DASH}</TableCell>
                                                        <TableCell>{charge.verdictFinding || charge.plea || DASH}</TableCell>
                                                    </>
                                                ) : (
                                                    <TableCell colSpan={3} sx={{ color: '#919EAB', fontStyle: 'italic' }}>
                                                        Charge detail not available for this docket
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                );
                            })}
                        </ReportTable>
                    </Collapse>
                ) : (
                    <EmptyState>No Criminal Records Found</EmptyState>
                )}

                {/* Charges the org's screening settings filtered out: live view only, never the PDF. */}
                {isLiveView && showFilteredCharges && findings?.filteredCharges && (
                    <FilteredCharges filteredCharges={findings.filteredCharges}/>
                )}
            </CardContent>
        </Card>
    )
}
